import React from "react";
import { useSelector } from "react-redux";
import "./SuperChatAI.scss";
import {
  ActionInfoAICard,
  ActionTableAICard,
  GenericSuperAICard,
  OrderStatusTableAICard,
  CreationFailuresAICard,
} from "./SuperAICards";
import { CHAT } from "../../../../util/constants";
import { RegionalLeadOrderTable } from "./SuperChatAIOrderTable/RegionalLeadOrderTable";

// Renders super agent AI response cards based on message content type
export const SuperChatAI = ({ 
  message, 
  onAddUserMessage, 
  onStatusClick,
  onCheckpointConfirm,
  isCheckpointSubmitting = false,
  checkpointSubmitError = null,
  checkpointSubmitSuccess = false,
}) => {
  // Get selected role from Redux
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const roleName = typeof selectedRole === 'object'
    ? (selectedRole?.roleName || selectedRole?.name || '').toUpperCase()
    : String(selectedRole || '').toUpperCase();

  const isRegionalLead = roleName === 'REGIONAL LEAD';

  // If message has creationFailures, use CreationFailuresAICard
  if (message.creationFailures && message.creationFailures.length > 0) {
    return (
      <CreationFailuresAICard
        message={message}
        title={CHAT.AXON_AI}
      />
    );
  }

  // If message has orderStatusData, use OrderStatusTableAICard (for Regional Lead Order by Status)
  if (message.orderStatusData && message.orderStatusData.length > 0) {
    return (
      <OrderStatusTableAICard
        message={message}
        title={CHAT.AXON_AI}
        onStatusClick={onStatusClick}
      />
    );
  }

  // If message has orders, use ActionTableAICard (which uses SuperChatAIOrderTable by default)
  if (message.orders && message.orders.length > 0) {
    return (
      <ActionTableAICard
        message={message}
        title={CHAT.AXON_AI}
        onAddUserMessage={onAddUserMessage}
        TableComponent={isRegionalLead ? RegionalLeadOrderTable : undefined}
      />
    );
  }

  // Get ui_fields for checkpoint handling
  const uiFields = message.ui_fields || [];
  const hasCheckpointPending = message.checkpoint_pending === true;
  const hasUpdateFlag = message.update === true;
  const hasUiFieldsFlag = message.ui_fields_flag === true;

  // Debug logging for update flow
  console.log("[SuperChatAI] Message flags:", {
    checkpoint_pending: message.checkpoint_pending,
    update: message.update,
    ui_fields_flag: message.ui_fields_flag,
    hasCheckpointPending,
    hasUpdateFlag,
    hasUiFieldsFlag,
    showGuidance: !hasCheckpointPending && hasUpdateFlag && hasUiFieldsFlag,
  });

  // If update and ui_fields_flag are true but checkpoint_pending is false,
  // show a guidance message to navigate to Order Details view
  if (!hasCheckpointPending && hasUpdateFlag && hasUiFieldsFlag) {
    const guidanceMessage = {
      ...message,
      text: "To update the requested field(s), please navigate to the Order Details view. Go to \"My Orders\" or \"Smart Suggestions\", then open the order you wish to modify. Thank you.",
      isGenericResponse: true,
    };
    return (
      <GenericSuperAICard
        message={guidanceMessage}
        title={CHAT.AXON_AI}
        onSuggestedQuestionClick={onAddUserMessage}
        checkpointPending={false}
        uiFields={[]}
        onCheckpointConfirm={onCheckpointConfirm}
        isCheckpointSubmitting={isCheckpointSubmitting}
        checkpointSubmitError={checkpointSubmitError}
        checkpointSubmitSuccess={checkpointSubmitSuccess}
      />
    );
  }

  // If message is a generic text response (from user input), use GenericSuperAICard
  if (message.isGenericResponse) {
    return (
      <GenericSuperAICard
        message={message}
        title={CHAT.AXON_AI}
        onSuggestedQuestionClick={onAddUserMessage}
        checkpointPending={hasCheckpointPending}
        uiFields={uiFields}
        onCheckpointConfirm={onCheckpointConfirm}
        isCheckpointSubmitting={isCheckpointSubmitting}
        checkpointSubmitError={checkpointSubmitError}
        checkpointSubmitSuccess={checkpointSubmitSuccess}
      />
    );
  }

  // Otherwise, use ActionInfoAICard for text-only messages
  return (
    <ActionInfoAICard
      message={message}
      title={CHAT.AXON_AI}
      onSuggestedQuestionClick={onAddUserMessage}
    />
  );
};
